// Modale levee a l'ouverture de l'app quand un prelevement a echoue.
//
// Deux etats distincts :
//  - grace en cours  : compte a rebours, acces encore complet, refermable ;
//  - grace ecoulee   : l'acces est deja retombe en Free, on propose de reactiver.
//
// Refermable dans les deux cas : bloquer l'app d'un utilisateur dont la carte a
// simplement expire le punirait deux fois. Elle revient a la session suivante.

import { useEffect, useState } from "react";
import { getCustomerPortalUrl, useQuery } from "wasp/client/operations";
import { Link as WaspRouterLink, routes } from "wasp/client/router";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import useTranslation from "../client/hooks/useTranslation";
import { usePaymentGrace } from "./usePaymentGrace";

/** Marqueur de session : la modale ne se releve pas apres une fermeture dans le
 *  meme onglet. `sessionStorage` et non `localStorage` : « a chaque connexion »
 *  doit vouloir dire une fois par session, pas une fois pour toutes. */
const DISMISS_KEY = "edgely.paymentGraceDismissed";

export default function PaymentGraceModal() {
  const t = useTranslation();
  const grace = usePaymentGrace();
  const [open, setOpen] = useState(false);

  const shouldShow = grace.isInGrace || grace.isExpired;

  useEffect(() => {
    if (!shouldShow) {
      setOpen(false);
      return;
    }
    // Une grace qui expire pendant la session doit relever la modale meme si le
    // rappel « en cours » avait ete ferme : le message a change de nature.
    const scope = grace.isExpired ? "expired" : "grace";
    if (sessionStorage.getItem(DISMISS_KEY) === scope) return;
    setOpen(true);
  }, [shouldShow, grace.isExpired]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, grace.isExpired ? "expired" : "grace");
    setOpen(false);
  };

  // Le portail Stripe n'est interrogé que si la modale peut s'afficher : inutile
  // d'ouvrir une session de facturation pour tous les utilisateurs a jour.
  const { data: portalUrl } = useQuery(getCustomerPortalUrl, undefined, {
    enabled: shouldShow,
    retry: false,
    onError: () => {
      // Sans portail (abonnement absent cote Stripe), on retombe sur /pricing.
    },
  });

  if (!shouldShow) return null;

  const isLastDay = grace.daysRemaining <= 1;
  const planName = grace.planName ?? "";

  const body = grace.isExpired
    ? t.paymentGrace.expiredModalBody
    : (isLastDay ? t.paymentGrace.modalBodyLastDay : t.paymentGrace.modalBody)
        .replace("{plan}", planName)
        .replace("{days}", String(grace.daysRemaining));

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : dismiss())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {grace.isExpired
              ? t.paymentGrace.expiredModalTitle
              : t.paymentGrace.modalTitle}
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">{body}</span>
            {!grace.isExpired && grace.endsAt && (
              <span className="block font-medium text-foreground">
                {t.paymentGrace.modalConsequence.replace(
                  "{date}",
                  grace.endsAt.toLocaleDateString(),
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={dismiss}>
            {t.paymentGrace.later}
          </Button>
          {portalUrl ? (
            <Button asChild>
              <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                {grace.isExpired
                  ? t.paymentGrace.expiredAction
                  : t.paymentGrace.updatePayment}
              </a>
            </Button>
          ) : (
            <Button asChild onClick={dismiss}>
              <WaspRouterLink to={routes.PricingRoute.to}>
                {grace.isExpired
                  ? t.paymentGrace.expiredAction
                  : t.paymentGrace.updatePayment}
              </WaspRouterLink>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
